import { z } from 'zod'
import { schemas } from '@repo/contracts'
import { parseDataOfType } from '@/common/parseDataType'
import { apiErrorHandler } from '../errorHandler'

// The generated schema is the contract, and `contracts:check` proves it matches the Go structs
// before anything ships. So a response schema does not also reject unknown keys at runtime: the
// only drift that survives the build gate is deploy skew, where rejecting costs the whole document
// rather than the one field the old bundle cannot render. Dropped rather than carried, because a
// key nothing can type should not reach client state. See ADR-176.
export const DocumentRevisionSchema = schemas.apihttp_DocumentRevisionResponse.strip()
export const DocumentSchema = schemas.apihttp_DocumentResponse
  .extend({
    revisions: z.array(DocumentRevisionSchema)
  })
  .strip()

export const ExportDocumentResponseSchema = schemas.apihttp_ExportDocumentResponse.strip()

export type DocumentRevision = z.infer<typeof DocumentRevisionSchema>
export type WorkspaceDocument = z.infer<typeof DocumentSchema>
export type ExportDocumentResponse = z.infer<typeof ExportDocumentResponseSchema>

export async function getDocument(projectId: number, documentId: number): Promise<WorkspaceDocument> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, DocumentSchema)
}

export async function renameDocument(projectId: number, documentId: number, title: string): Promise<WorkspaceDocument> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ title })
  })
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, DocumentSchema)
}

// A successful delete answers 204 with an empty body, so resolve without reading it: calling
// `.json()` on an empty body throws.
export async function deleteDocument(projectId: number, documentId: number): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
}

// The export body is optional: an absent one means the document's current revision. Omit it
// altogether rather than sending `{}`, so an unrequested version can never reach the wire as
// `{"versionId": undefined}` or as the string `undefined`.
export async function exportDocument(
  projectId: number,
  documentId: number,
  versionId?: number
): Promise<ExportDocumentResponse> {
  const request: RequestInit = {
    method: 'POST',
    credentials: 'same-origin'
  }
  if (versionId !== undefined) {
    request.headers = { 'Content-Type': 'application/json' }
    request.body = JSON.stringify({ versionId })
  }
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/export`, request)
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, ExportDocumentResponseSchema)
}
